import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { Toast } from 'primereact/toast';
import { Card } from 'primereact/card';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Calendar } from 'primereact/calendar';
import { InputNumber } from 'primereact/inputnumber';
import { Button } from 'primereact/button';
import { FileUpload } from 'primereact/fileupload';
import { ProgressSpinner } from 'primereact/progressspinner';
import { getBranches, getVoucherExpenses, createVoucher } from '../utilities/api';
import { amountToWordsINR } from './amountToWordsINR';
import '../masters/CategoryMaster.css';

const VOUCHER_DATE_DISPLAY = 'dd-mm-yy';

const AddVoucher = () => {
    const toast = useRef(null);
    const navigate = useNavigate();
    const fileUploadRef = useRef(null);

    const [loading, setLoading] = useState(false);
    const [loadingMeta, setLoadingMeta] = useState(true);

    const [branchOptions, setBranchOptions] = useState([]);
    const [expenseOptions, setExpenseOptions] = useState([]);

    const [branchId, setBranchId] = useState(null);
    const [expaId, setExpaId] = useState(null);
    const [voucherDate, setVoucherDate] = useState(new Date());
    const [voucherTime, setVoucherTime] = useState(new Date());
    const [customerName, setCustomerName] = useState('');
    const [voucherAmount, setVoucherAmount] = useState(null);
    const [voucherAmountWords, setVoucherAmountWords] = useState('');
    const [description, setDescription] = useState('');
    const [voucherFile, setVoucherFile] = useState(null);

    const userId = localStorage.getItem('userid') || '';

    useEffect(() => {
        const load = async () => {
            setLoadingMeta(true);
            const clientId = localStorage.getItem('clientid');
            try {
                const branches = await getBranches(clientId);
                const arr = Array.isArray(branches) ? branches : branches?.branchs || [];
                setBranchOptions(arr.map((b) => ({
                    label: b.branch_name || b.branchname || b.name || String(b.branch_id),
                    value: String(b.branch_id)
                })));
            } catch {
                toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Could not load branches' });
            }
            try {
                const res = await getVoucherExpenses({ client_id: clientId });
                const responseBranches = Array.isArray(res?.branchs) ? res.branchs : [];
                if (responseBranches.length) {
                    setBranchOptions(responseBranches.map((b) => ({
                        label: b.branch_name || b.branchname || b.name || String(b.branch_id),
                        value: String(b.branch_id)
                    })));
                }
                const raw = res?.expnses ?? res?.expenses ?? res?.data ?? res;
                const list = Array.isArray(raw) ? raw : [];
                setExpenseOptions(list.map((e) => ({
                    label: e.expanses_name || e.expa_name || e.expense_name || e.name || String(e.expa_id),
                    value: String(e.expa_id)
                })));
            } catch {
                setExpenseOptions([]);
                toast.current?.show({ severity: 'warn', summary: 'Expenses', detail: 'Could not load expense list.' });
            } finally {
                setLoadingMeta(false);
            }
        };
        load();
    }, []);

    useEffect(() => {
        setVoucherAmountWords(amountToWordsINR(voucherAmount));
    }, [voucherAmount]);

    const canSubmit = useMemo(
        () => branchId && expaId && voucherDate && voucherTime && customerName.trim() && voucherAmount != null && userId,
        [branchId, expaId, voucherDate, voucherTime, customerName, voucherAmount, userId]
    );

    const onFileSelect = (e) => setVoucherFile(e.files?.[0] || null);

    const onClearFile = () => {
        setVoucherFile(null);
        fileUploadRef.current?.clear();
    };

    const handleReset = () => {
        setBranchId(null);
        setExpaId(null);
        setVoucherDate(new Date());
        setVoucherTime(new Date());
        setCustomerName('');
        setVoucherAmount(null);
        setVoucherAmountWords('');
        setDescription('');
        setVoucherFile(null);
        fileUploadRef.current?.clear();
    };

    const buildPayload = () => {
        const fd = new FormData();
        fd.append('vocher_date', moment(voucherDate).format('YYYY-MM-DD'));
        fd.append('vocher_time', moment(voucherTime).format('HH:mm:ss'));
        fd.append('customer_name', customerName.trim());
        fd.append('vocher_amount', String(voucherAmount));
        fd.append('vocher_amount_words', voucherAmountWords || '');
        fd.append('vocher_discriptions', description);
        fd.append('branch_id', branchId);
        fd.append('userId', userId);
        fd.append('status', 'processing');
        fd.append('expa_id', expaId);
        if (voucherFile) fd.append('vocher_file', voucherFile);
        return fd;
    };

    const handleSubmit = async () => {
        if (!canSubmit) {
            toast.current?.show({ severity: 'warn', summary: 'Validation', detail: 'Please fill branch, expense, date, time, customer, and amount.' });
            return;
        }
        setLoading(true);
        try {
            const res = await createVoucher(buildPayload());
            const ok = res?.status === true || res?.status === 'success' || res?.success === true;
            if (ok) {
                toast.current?.show({ severity: 'success', summary: 'Saved', detail: res?.message || 'Voucher created' });
                navigate('/voucherlist');
            } else {
                toast.current?.show({ severity: 'error', summary: 'Error', detail: res?.message || res?.error || 'Could not create voucher' });
            }
        } catch (err) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: err?.response?.data?.message || err.message || 'Request failed' });
        } finally {
            setLoading(false);
        }
    };

    if (loadingMeta) {
        return (
            <div className="flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                <ProgressSpinner />
            </div>
        );
    }

    return (
        <div className="masters-page-outer">
            <Toast ref={toast} />

            {/* Page header */}
            <div className="masters-page-header">
                <h2>Add Voucher</h2>
                <p>Create a new cash voucher entry</p>
            </div>

            {/* Form card */}
            <Card className="bill-reports-container">
                <div className="p-fluid formgrid grid">

                    <div className="field col-12 md:col-6">
                        <label htmlFor="branch_id" className="font-medium">Branch</label>
                        <Dropdown id="branch_id" value={branchId} options={branchOptions} onChange={(e) => setBranchId(e.value)} placeholder="Select branch" filter showClear />
                    </div>

                    <div className="field col-12 md:col-6">
                        <label htmlFor="expa_id" className="font-medium">Expense</label>
                        <Dropdown id="expa_id" value={expaId} options={expenseOptions} onChange={(e) => setExpaId(e.value)} placeholder="Select expense" filter showClear />
                    </div>

                    <div className="field col-12 md:col-6">
                        <label htmlFor="vocher_date" className="font-medium">Voucher Date</label>
                        <Calendar id="vocher_date" value={voucherDate} onChange={(e) => setVoucherDate(e.value)} showIcon dateFormat={VOUCHER_DATE_DISPLAY} />
                    </div>

                    <div className="field col-12 md:col-6">
                        <label htmlFor="vocher_time" className="font-medium">Voucher Time</label>
                        <Calendar id="vocher_time" value={voucherTime} onChange={(e) => setVoucherTime(e.value)} timeOnly hourFormat="24" showIcon />
                    </div>

                    <div className="field col-12 md:col-6">
                        <label htmlFor="customer_name" className="font-medium">Customer Name</label>
                        <InputText id="customer_name" value={customerName} onChange={(e) => setCustomerName(e.target.value)} placeholder="Enter customer name" />
                    </div>

                    <div className="field col-12 md:col-6">
                        <label htmlFor="vocher_amount" className="font-medium">Voucher Amount</label>
                        <InputNumber id="vocher_amount" value={voucherAmount} onValueChange={(e) => setVoucherAmount(e.value)} mode="decimal" minFractionDigits={0} maxFractionDigits={2} useGrouping={false} placeholder="0.00" />
                    </div>

                    <div className="field col-12 md:col-6">
                        <label className="font-medium">Amount in Words</label>
                        <InputTextarea value={voucherAmountWords} readOnly rows={2} className="w-full" />
                    </div>

                    <div className="field col-12 md:col-6">
                        <label className="font-medium">
                            Attachment&nbsp;<span style={{ fontWeight: 400, color: '#6b7280', fontSize: '0.82rem' }}>(image / PDF, optional)</span>
                        </label>
                        <div className="flex align-items-center gap-2 flex-wrap">
                            <FileUpload
                                ref={fileUploadRef}
                                mode="basic"
                                name="vocher_file"
                                accept="image/*"
                                maxFileSize={10000000}
                                chooseLabel={voucherFile ? voucherFile.name : 'Choose file'}
                                onSelect={onFileSelect}
                                className="p-button-sm"
                                auto={false}
                            />
                            {voucherFile && (
                                <Button type="button" label="Clear" icon="pi pi-times" className="p-button-text p-button-sm p-button-danger" onClick={onClearFile} />
                            )}
                        </div>
                        {voucherFile && (
                            <small className="text-color-secondary mt-1 block">{voucherFile.name} ({(voucherFile.size / 1024).toFixed(1)} KB)</small>
                        )}
                    </div>

                    <div className="field col-12">
                        <label htmlFor="vocher_discriptions" className="font-medium">Voucher Description</label>
                        <InputTextarea id="vocher_discriptions" value={description} onChange={(e) => setDescription(e.target.value)} rows={3} className="w-full" placeholder="Notes or description" />
                    </div>

                    <div className="field col-12">
                        <hr style={{ borderColor: '#e5d8f5', margin: '0.5rem 0 1rem' }} />
                        <div className="flex gap-2">
                            <Button label="Submit" icon="pi pi-check" className="sub-dialog-save" onClick={handleSubmit} loading={loading} disabled={!canSubmit} />
                            <Button label="Clear" icon="pi pi-refresh" className="sub-dialog-cancel" onClick={handleReset} disabled={loading} />
                        </div>
                    </div>

                </div>
            </Card>
        </div>
    );
};

export default AddVoucher;
